/* eslint-disable spire/export-styles */
import { FLOALA_TOOLBAR_COLLAPSED, FLOALA_TOOLBAR_EXPANDED, FROALA_CONFIG } from "@affinity/constants/froalaConfig";
import { colours } from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import { breakpointWidth } from "@affinity/constants/StyleHelpers";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import parse from "html-react-parser";
import * as React from "react";
import styled from "styled-components";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    padding = "padding",
    content = "content",
}

interface ComponentProps {
    fields: {
        [fields.padding]?: boolean;
        [fields.content]: string;
    };
    css?: any;
}

type Props = WidgetProps & ComponentProps;

export interface RichContentStyles {
    wrapper?: InjectableCss;
}

const richContentStyles = (useLargePadding: boolean) => {
    const marginValPx = useLargePadding ? 20 : 10;
    return css`
        color: ${colours.black};
        h2 {
            font-size: ${styleHelpers.rem(24)};
            line-height: 1.25;
            font-weight: 700;
            @media screen and (min-width: ${breakpointWidth("lg")}) {
                font-size: ${styleHelpers.rem(32)};
                line-height: 1.25;
            }
        }
        h3 {
            font-size: ${styleHelpers.rem(20)};
            line-height: 1.25;
            font-weight: 700;
            @media screen and (min-width: ${breakpointWidth("lg")}) {
                font-size: ${styleHelpers.rem(24)};
                line-height: 1.25;
            }
        }
        h4 {
            font-size: ${styleHelpers.rem(18)};
            line-height: 1.25;
            font-weight: 700;
            @media screen and (min-width: ${breakpointWidth("lg")}) {
                font-size: ${styleHelpers.rem(20)};
                line-height: 1.25;
            }
        }
        p,
        ul,
        ol {
            font-size: ${styleHelpers.rem(16)};
            font-weight: 300;
            line-height: 1.625;
            @media screen and (min-width: ${breakpointWidth("lg")}) {
                font-size: ${styleHelpers.rem(18)};
            }
        }
        p,
        h2,
        h3,
        h4 {
            margin: 0 0 ${styleHelpers.rem(marginValPx)};
        }
        ul,
        ol {
            padding: 0 0 0 ${styleHelpers.rem(10 + marginValPx)};
            li {
                margin: 0 0 ${styleHelpers.rem(4)};
                &:nth-last-of-type(1) {
                    margin: 0;
                }
            }
        }
    `;
};

// keeping it as a standard component without widget props to use in our own pages
export const RichContentComponent: React.FunctionComponent<ComponentProps> = props => {
    if (!props.fields.content) {
        return null;
    }
    const useLargePadding = props.fields.padding ?? false;
    const combinedStyles = {
        wrapper: {
            css: css`
                ${richContentStyles(useLargePadding)}
                ${props.css}
            `,
        },
    };
    const styles = useMergeStyles("richContent", combinedStyles);

    return <ContentWrapper {...styles.wrapper}>{parse(props.fields.content, parserOptions)}</ContentWrapper>;
};

export const RichContent: React.FunctionComponent<Props> = props => {
    return <RichContentComponent fields={props.fields} css={props.css} />;
};

// generate wysiwyg cms field
export const richTextContentField = (
    name: string,
    displayName: string,
    tab: TabDefinition | undefined = undefined,
): FieldDefinition => {
    return {
        name,
        displayName,
        editorTemplate: "RichTextField",
        defaultValue: "",
        tab,
        fieldType: "Contextual",
        validate: (html: string) => {
            const arrayOfStyles = extractStylesToArray(html);
            return arrayOfStyles?.every(style => isSafe(style))
                ? null
                : "The HTML contains invalid styles that prevent it from being saved";
        },
        extendedConfig: {
            ...FROALA_CONFIG,
        },
        collapsedToolbarButtons: FLOALA_TOOLBAR_COLLAPSED,
        expandedToolbarButtons: FLOALA_TOOLBAR_EXPANDED,
    };
};

const widgetModule: WidgetModule = {
    component: RichContentComponent,
    definition: {
        group: "Basic",
        icon: "text",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.padding,
                editorTemplate: "CheckboxField",
                displayName: "Use large padding?",
                defaultValue: false,
            },
            richTextContentField(fields.content, "Content"),
        ],
    },
};

export default widgetModule;

export const ContentWrapper = styled.div<{} & InjectableCss>`
    & > *:last-child:not(ul),
    & > *:last-child:not(ol) {
        margin: 0;
    }
    ul,
    ol {
        margin-top: 0;
    }
    ${injectCss}
`;
